import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useForm } from "@tanstack/react-form";
import z from "zod";
import {
  Users,
  UserPlus,
  Trash2,
  Shield,
  User,
  Lock,
  X,
  Loader2,
  Calendar,
  KeyRound,
  Pencil,
  Info,
} from "lucide-react";

import { orpc } from "@/utils/orpc";
import { authClient } from "@/lib/auth-client";
import { StaffPermission, StaffPermissionPreset } from "@pos-vidyon/api/permissions";

export const Route = createFileRoute("/_auth/admin/staff")({
  component: StaffManagementPage,
});

interface OrganizationMember {
  id: string;
  role: string;
  createdAt: Date;
  userId: string;
  organizationId: string;
  staffRoleId?: string | null;
  user: {
    id: string;
    name: string;
    email: string;
    image?: string | null;
    username?: string | null;
  };
}

const PERMISSIONS = [
  {
    bit: StaffPermission.TAKE_ORDERS,
    name: "Sipariş Yönetimi",
    desc: "Sipariş alabilir ve yönetebilir",
  },
  {
    bit: StaffPermission.COLLECT_PAYMENT,
    name: "Ödeme Yönetimi",
    desc: "Ödeme alabilir, masayı kapatabilir",
  },
  {
    bit: StaffPermission.VIEW_KITCHEN,
    name: "Mutfak Ekranı",
    desc: "Mutfak ekranını görebilir ve yönetebilir",
  },
  {
    bit: StaffPermission.MANAGE_MENU,
    name: "Menü Yönetimi",
    desc: "Ürünleri ve kategorileri düzenleyebilir",
  },
  {
    bit: StaffPermission.MANAGE_STAFF,
    name: "Personel Yönetimi",
    desc: "Personel ekleyebilir ve yetkilendirebilir",
  },
  {
    bit: StaffPermission.VIEW_REVENUE,
    name: "Ciro & Rapor Görme",
    desc: "Finansal raporları ve ciroları görebilir",
  },
];

const resolvePermissions = (memberItem: OrganizationMember, customRoles: any[]) => {
  if (memberItem.role === "owner") return StaffPermissionPreset.OWNER;
  if (memberItem.staffRoleId) {
    const customRole = customRoles.find((r) => r.id === memberItem.staffRoleId);
    if (customRole) return customRole.permissions;
  }
  return memberItem.role === "admin" ? StaffPermissionPreset.ADMIN : StaffPermissionPreset.STAFF;
};

const getMemberRoleLabel = (memberItem: OrganizationMember, customRoles: any[]) => {
  if (memberItem.role === "owner") {
    return {
      label: "Restoran Sahibi",
      className: "bg-purple-50 text-purple-700 border border-purple-100",
    };
  }

  if (memberItem.staffRoleId) {
    const customRole = customRoles.find((r) => r.id === memberItem.staffRoleId);
    if (customRole) {
      return {
        label: customRole.name,
        className: "bg-amber-50 text-amber-700 border border-amber-100",
      };
    }
  }

  const permissions = resolvePermissions(memberItem, customRoles);

  if (memberItem.role === "admin" && permissions === StaffPermissionPreset.ADMIN) {
    return {
      label: "Yönetici",
      className: "bg-blue-50 text-blue-700 border border-blue-100",
    };
  }

  if (memberItem.role === "member" && permissions === StaffPermissionPreset.STAFF) {
    return {
      label: "Personel / Garson",
      className: "bg-gray-100 text-gray-700 border border-gray-200",
    };
  }

  if (memberItem.role === "member" && permissions === StaffPermissionPreset.CASHIER) {
    return {
      label: "Kasiyer",
      className: "bg-emerald-50 text-emerald-700 border border-emerald-100",
    };
  }

  return {
    label: memberItem.role === "admin" ? "Yönetici (Özel)" : "Personel (Özel)",
    className: "bg-amber-50 text-amber-700 border border-amber-100",
  };
};

function StaffManagementPage() {
  const { session } = Route.useRouteContext();
  const { data: activeOrg } = authClient.useActiveOrganization();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<OrganizationMember | null>(null);
  const [editRole, setEditRole] = useState<"admin" | "member">("member");
  const [editRoleId, setEditRoleId] = useState<string | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  // States for dynamic/inline custom role creation
  const [isCreatingCustomRole, setIsCreatingCustomRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [newRolePermissions, setNewRolePermissions] = useState<number>(StaffPermissionPreset.STAFF);

  // Query staff members list using Better Auth's built-in endpoint
  const {
    data: staffMembers = [],
    refetch,
    isLoading,
  } = useQuery<OrganizationMember[]>({
    queryKey: ["staff-members", activeOrg?.id],
    queryFn: async () => {
      if (!activeOrg?.id) return [];
      const res = await authClient.organization.listMembers({
        query: {
          organizationId: activeOrg.id,
        },
      });
      if (res.error) {
        throw new Error(res.error.message || "Personel listesi yüklenemedi.");
      }
      return (res.data?.members || []) as unknown as OrganizationMember[];
    },
    enabled: !!activeOrg?.id,
  });

  // Query custom roles list
  const { data: customRoles = [], refetch: refetchRoles } = useQuery(
    orpc.restaurant.getStaffRoles.queryOptions(),
  );

  // Mutation to create custom role
  const createRoleMutation = useMutation(
    orpc.restaurant.createStaffRole.mutationOptions({
      onSuccess: (data) => {
        toast.success("Yeni rol başarıyla oluşturuldu.");
        refetchRoles();
        if (isAddModalOpen) {
          form.setFieldValue("staffRoleId", data.roleId);
        } else if (isEditModalOpen) {
          setEditRoleId(data.roleId);
        }
        setIsCreatingCustomRole(false);
      },
      onError: (err: any) => {
        toast.error(err.message || "Rol oluşturulurken bir hata oluştu.");
      },
    }),
  );

  // Mutation to add new member using custom endpoint (combines signUpEmail + addMember)
  const createMutation = useMutation(
    orpc.restaurant.createStaffMember.mutationOptions({
      onSuccess: () => {
        toast.success("Personel başarıyla eklendi.");
        setIsAddModalOpen(false);
        form.reset();
        refetch();
      },
      onError: (err: any) => {
        toast.error(err.message || "Personel eklenirken bir hata oluştu.");
      },
    }),
  );

  // Mutation to update existing member
  const updateMutation = useMutation(
    orpc.restaurant.updateStaffMember.mutationOptions({
      onSuccess: () => {
        toast.success("Personel başarıyla güncellendi.");
        setIsEditModalOpen(false);
        refetch();
      },
      onError: (err: any) => {
        toast.error(err.message || "Personel güncellenirken bir hata oluştu.");
      },
    }),
  );

  // TanStack Form configuration
  const form = useForm({
    defaultValues: {
      name: "",
      username: "",
      password: "",
      role: "member" as "admin" | "member",
      staffRoleId: "system_staff" as string | null,
    },
    onSubmit: async ({ value }) => {
      createMutation.mutate({
        name: value.name.trim(),
        username: value.username.trim(),
        password: value.password,
        role: value.role,
        staffRoleId: value.staffRoleId,
      });
    },
    validators: {
      onSubmit: z.object({
        name: z.string().min(2, "Personel adı en az 2 karakter olmalıdır"),
        username: z.string().min(2, "Kullanıcı adı en az 2 karakter olmalıdır"),
        password: z.string().min(8, "Şifre en az 8 karakter olmalıdır"),
        role: z.enum(["admin", "member"]),
        staffRoleId: z.string().nullable(),
      }),
    },
  });

  const handleRemove = async (memberId: string, memberName: string) => {
    if (!activeOrg?.id) return;
    if (confirm(`${memberName} isimli personeli silmek istediğinize emin misiniz?`)) {
      setIsRemoving(true);
      try {
        const res = await authClient.organization.removeMember({
          memberIdOrEmail: memberId,
          organizationId: activeOrg.id,
        });

        if (res.error) {
          toast.error(res.error.message || "Personel silinemedi.");
        } else {
          toast.success("Personel başarıyla silindi.");
          refetch();
        }
      } catch {
        toast.error("İşlem gerçekleştirilemedi.");
      } finally {
        setIsRemoving(false);
      }
    }
  };

  return (
    <div className="space-y-6 pb-6 animate-in fade-in duration-300">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-gray-900">Personel Yönetimi</h1>
          <p className="text-sm text-gray-500">
            Kafenizde POS terminallerini kullanacak personel hesaplarını yönetin.
          </p>
        </div>
        <button
          onClick={() => {
            form.reset();
            setIsAddModalOpen(true);
          }}
          className="flex items-center gap-1.5 rounded-xl bg-gradient-to-b from-[#2d2e32] to-[#1e1f22] text-white px-4 py-2.5 text-xs font-bold shadow-md hover:from-[#383a3f] hover:to-[#24252a] active:scale-[0.99] transition-all cursor-pointer select-none"
        >
          <UserPlus className="size-4" />
          <span>Yeni Personel Hesabı</span>
        </button>
      </div>

      {/* active terminal status card */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="flex size-12 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
            <KeyRound className="size-6" />
          </div>
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">
              Aktif Terminal Kodu
            </p>
            <p className="text-sm font-black text-emerald-700 uppercase tracking-widest mt-1 bg-emerald-50/50 px-2 py-0.5 rounded-md border border-emerald-100 w-fit select-all">
              {activeOrg?.slug ?? "N/A"}
            </p>
          </div>
        </div>
      </div>

      {/* Members table card */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="size-8 text-blue-600 animate-spin" />
            <span className="text-sm text-gray-400 font-medium">
              Personel listesi yükleniyor...
            </span>
          </div>
        ) : staffMembers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center px-4">
            <Users className="size-12 text-gray-300 mb-3" />
            <h3 className="text-base font-bold text-gray-800">Personel Bulunamadı</h3>
            <p className="text-sm text-gray-500 max-w-[340px] mt-1 leading-relaxed">
              Bu restorana henüz hiçbir personel veya yönetici eklenmemiş. Yeni personel ekleyerek
              başlayabilirsiniz.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/55 text-xs font-bold text-gray-400 uppercase tracking-wider select-none">
                  <th className="px-6 py-4">İsim / Görüntü</th>
                  <th className="px-6 py-4">Giriş Kullanıcı Adı</th>
                  <th className="px-6 py-4">Rol</th>
                  <th className="px-6 py-4">Kayıt Tarihi</th>
                  <th className="px-6 py-4 text-right">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-sm text-gray-700">
                {staffMembers.map((memberItem) => {
                  const isSelf = memberItem.user.id === session.data?.user.id;
                  const isOwner = memberItem.role === "owner";
                  const roleBadge = getMemberRoleLabel(memberItem, customRoles);

                  return (
                    <tr key={memberItem.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex size-9 items-center justify-center rounded-full bg-gradient-to-tr from-gray-100 to-gray-200 font-bold text-gray-600 shadow-inner">
                            {memberItem.user.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <span className="font-semibold text-gray-900 block">
                              {memberItem.user.name}
                            </span>
                            {isSelf && (
                              <span className="text-[10px] font-bold text-blue-600 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded mt-0.5 w-fit block uppercase tracking-wider">
                                Siz
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4  text-xs text-gray-500">
                        {memberItem.user.username || memberItem.user.email}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${roleBadge.className}`}
                        >
                          {isOwner && <Shield className="size-3" />}
                          {!isOwner && memberItem.role === "admin" && <Shield className="size-3" />}
                          <span>{roleBadge.label}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs text-gray-400 font-medium">
                        <span className="flex items-center gap-1">
                          <Calendar className="size-3.5" />
                          {new Date(memberItem.createdAt).toLocaleDateString("tr-TR", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-1.5">
                          {!isOwner && !isSelf && (
                            <>
                              <button
                                onClick={() => {
                                  setEditingMember(memberItem);
                                  setEditRole(memberItem.role as "admin" | "member");
                                  setEditRoleId(memberItem.staffRoleId || null);
                                  setIsEditModalOpen(true);
                                }}
                                className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors cursor-pointer"
                              >
                                <Pencil className="size-4" />
                              </button>
                              <button
                                onClick={() => handleRemove(memberItem.id, memberItem.user.name)}
                                disabled={isRemoving}
                                className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-50 cursor-pointer"
                              >
                                <Trash2 className="size-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Staff Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-gray-100 pb-4">
              <div className="flex items-center gap-2">
                <UserPlus className="size-5 text-blue-600" />
                <h3 className="text-lg font-bold text-gray-900">Yeni Personel Hesabı</h3>
              </div>
              <button
                onClick={() => setIsAddModalOpen(false)}
                className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <X className="size-5" />
              </button>
            </div>

            {/* Modal Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                form.handleSubmit();
              }}
              className="mt-4 space-y-4"
            >
              {/* Display Name */}
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 select-none">
                  Personel Adı Soyadı
                </label>
                <form.Field name="name">
                  {(field) => (
                    <div>
                      <div className="relative flex items-center">
                        <User className="absolute left-3.5 size-4 text-gray-400" />
                        <input
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          type="text"
                          required
                          placeholder="Örn: Ahmet Yılmaz"
                          className="w-full h-11 pl-11 pr-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:bg-white focus:ring-2 focus:ring-blue-600/10 focus:border-blue-600 transition-all duration-200"
                        />
                      </div>
                      {field.state.meta.errors.length > 0 && (
                        <p className="text-[10px] text-red-500 mt-1 font-semibold pl-2">
                          {field.state.meta.errors
                            .map((error: any) => error?.message || error)
                            .join(", ")}
                        </p>
                      )}
                    </div>
                  )}
                </form.Field>
              </div>

              {/* Username */}
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 select-none">
                  Giriş Kullanıcı Adı
                </label>
                <form.Field name="username">
                  {(field) => (
                    <div>
                      <div className="relative flex items-center">
                        <span className="absolute left-3.5 text-sm font-bold text-gray-400 select-none">
                          @
                        </span>
                        <input
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          type="text"
                          required
                          placeholder="Örn: ahmet_garson"
                          className="w-full h-11 pl-8 pr-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:bg-white focus:ring-2 focus:ring-blue-600/10 focus:border-blue-600 transition-all duration-200"
                        />
                      </div>
                      {field.state.meta.errors.length > 0 && (
                        <p className="text-[10px] text-red-500 mt-1 font-semibold pl-2">
                          {field.state.meta.errors
                            .map((error: any) => error?.message || error)
                            .join(", ")}
                        </p>
                      )}
                    </div>
                  )}
                </form.Field>
                <p className="text-[10px] text-gray-400 mt-1 pl-2">
                  POS girişinde kullanılacak benzersiz ad. Sonuna otomatik olarak{" "}
                  <span className="font-semibold">_{activeOrg?.slug}</span> eklenecektir.
                </p>
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 select-none">
                  Giriş Şifresi
                </label>
                <form.Field name="password">
                  {(field) => (
                    <div>
                      <div className="relative flex items-center">
                        <Lock className="absolute left-3.5 size-4 text-gray-400" />
                        <input
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          type="password"
                          required
                          minLength={8}
                          placeholder="•••••••• (Min 8 karakter)"
                          className="w-full h-11 pl-11 pr-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:bg-white focus:ring-2 focus:ring-blue-600/10 focus:border-blue-600 transition-all duration-200"
                        />
                      </div>
                      {field.state.meta.errors.length > 0 && (
                        <p className="text-[10px] text-red-500 mt-1 font-semibold pl-2">
                          {field.state.meta.errors
                            .map((error: any) => error?.message || error)
                            .join(", ")}
                        </p>
                      )}
                    </div>
                  )}
                </form.Field>
              </div>

              {/* Preset Role Select */}
              <form.Field name="staffRoleId">
                {(field) => (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider select-none">
                        Rol / Pozisyon
                      </label>

                      {/* Inline role creator trigger */}
                      {isCreatingCustomRole ? (
                        <button
                          type="button"
                          onClick={() => setIsCreatingCustomRole(false)}
                          className="text-xs text-red-500 hover:text-red-600 font-bold cursor-pointer"
                        >
                          İptal
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setNewRoleName("");
                            setNewRolePermissions(StaffPermissionPreset.STAFF);
                            setIsCreatingCustomRole(true);
                          }}
                          className="text-xs text-blue-600 hover:text-blue-700 font-bold cursor-pointer"
                        >
                          + Yeni Özel Rol Tanımla
                        </button>
                      )}
                    </div>

                    {/* Dynamic Role Creator Form (Inline accordion) */}
                    {isCreatingCustomRole && (
                      <div className="border border-blue-100 rounded-xl p-3.5 bg-blue-50/20 space-y-3 animate-in slide-in-from-top-2 duration-200">
                        <div>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                            Özel Rol İsmi
                          </label>
                          <input
                            type="text"
                            placeholder="Örn: Garson Şefi, Dönerci"
                            value={newRoleName}
                            onChange={(e) => setNewRoleName(e.target.value)}
                            className="w-full h-10 px-3 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600/10"
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                            Bu Rolün Yetkileri
                          </label>
                          <div className="grid grid-cols-1 gap-2 max-h-[140px] overflow-y-auto pr-1">
                            {PERMISSIONS.map((perm) => {
                              const isChecked = (newRolePermissions & perm.bit) !== 0;
                              return (
                                <label
                                  key={perm.bit}
                                  className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none"
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => {
                                      setNewRolePermissions(
                                        isChecked
                                          ? newRolePermissions & ~perm.bit
                                          : newRolePermissions | perm.bit,
                                      );
                                    }}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <span>{perm.name}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>

                        <button
                          type="button"
                          disabled={!newRoleName.trim() || createRoleMutation.isPending}
                          onClick={() => {
                            createRoleMutation.mutate({
                              name: newRoleName.trim(),
                              permissions: newRolePermissions,
                            });
                          }}
                          className="w-full h-9 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                        >
                          {createRoleMutation.isPending ? (
                            <Loader2 className="size-3.5 animate-spin" />
                          ) : (
                            "Rolü Kaydet ve Seç"
                          )}
                        </button>
                      </div>
                    )}

                    {!isCreatingCustomRole && (
                      <>
                        <select
                          id={field.name}
                          name={field.name}
                          value={field.state.value || "system_staff"}
                          onBlur={field.handleBlur}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "system_admin") {
                              form.setFieldValue("role", "admin");
                            } else {
                              form.setFieldValue("role", "member");
                            }
                            field.handleChange(val || "system_staff");
                          }}
                          className="w-full h-11 px-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 outline-none focus:bg-white focus:ring-2 focus:ring-blue-600/10 focus:border-blue-600 transition-all duration-200"
                        >
                          <option value="system_staff">Personel / Garson (Varsayılan)</option>
                          <option value="system_cashier">Kasiyer (Varsayılan)</option>
                          <option value="system_admin">Yönetici (Varsayılan)</option>
                          {customRoles.map((role: any) => (
                            <option key={role.id} value={role.id}>
                              {role.name}
                            </option>
                          ))}
                        </select>

                        {/* Read-only Permissions Preview */}
                        <div className="border border-gray-200/60 rounded-xl p-3 bg-gray-50/50">
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider select-none mb-2">
                            Bu Rolün Sahip Olduğu Yetkiler
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {(() => {
                              const selectedVal = field.state.value || "system_staff";
                              let perms: number = StaffPermissionPreset.STAFF;
                              if (selectedVal === "system_cashier")
                                perms = StaffPermissionPreset.CASHIER;
                              else if (selectedVal === "system_admin")
                                perms = StaffPermissionPreset.ADMIN;
                              else {
                                const customRole = customRoles.find(
                                  (r: any) => r.id === selectedVal,
                                );
                                if (customRole) perms = customRole.permissions;
                              }

                              const activePerms = PERMISSIONS.filter((p) => (perms & p.bit) !== 0);
                              if (activePerms.length === 0) {
                                return (
                                  <span className="text-[10px] text-gray-400">
                                    Hiçbir yetki atanmamış.
                                  </span>
                                );
                              }
                              return activePerms.map((p) => (
                                <span
                                  key={p.bit}
                                  className="inline-flex text-[10px] font-bold text-blue-700 bg-blue-50/50 border border-blue-100 px-2 py-0.5 rounded-md"
                                >
                                  {p.name}
                                </span>
                              ));
                            })()}
                          </div>
                        </div>

                        <p className="text-[10px] text-gray-400 flex items-start gap-1 bg-gray-50 p-2 rounded-lg border border-gray-100">
                          <Info className="size-3.5 text-blue-500 shrink-0 mt-0.5" />
                          <span>
                            {form.state.values.role === "admin"
                              ? "Yöneticiler hem web yönetim paneline hem de POS terminallerine giriş yapabilir."
                              : "Personeller web yönetim paneline giriş yapamaz, sadece POS terminalini kullanabilir."}
                          </span>
                        </p>
                      </>
                    )}
                  </div>
                )}
              </form.Field>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-3 border-t border-gray-100 mt-4">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="h-11 px-4 border border-gray-200 text-gray-500 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="h-11 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold flex items-center gap-1.5 shadow-md hover:shadow-lg disabled:opacity-50 active:scale-[0.98] transition-all cursor-pointer"
                >
                  {createMutation.isPending && (
                    <Loader2 className="size-4 animate-spin text-white" />
                  )}
                  <span>Personel Oluştur</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Staff Modal */}
      {isEditModalOpen && editingMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-gray-100 pb-4">
              <div className="flex items-center gap-2">
                <Pencil className="size-5 text-blue-600" />
                <h3 className="text-lg font-bold text-gray-900">Personel Yetkilerini Düzenle</h3>
              </div>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <X className="size-5" />
              </button>
            </div>

            {/* Modal Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateMutation.mutate({
                  memberId: editingMember.id,
                  role: editRole,
                  staffRoleId: editRoleId,
                });
              }}
              className="mt-4 space-y-4"
            >
              {/* Display Name (Read-Only) */}
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 select-none">
                  Personel Adı Soyadı
                </label>
                <div className="relative flex items-center">
                  <User className="absolute left-3.5 size-4 text-gray-400" />
                  <input
                    type="text"
                    disabled
                    value={editingMember.user.name}
                    className="w-full h-11 pl-11 pr-4 bg-gray-100/80 border border-gray-200 rounded-xl text-sm text-gray-500 outline-none cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Username (Read-Only) */}
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 select-none">
                  Giriş Kullanıcı Adı
                </label>
                <div className="relative flex items-center">
                  <span className="absolute left-3.5 text-sm font-bold text-gray-400 select-none">
                    @
                  </span>
                  <input
                    type="text"
                    disabled
                    value={(editingMember.user.username || "").split("_")[0]}
                    className="w-full h-11 pl-8 pr-4 bg-gray-100/80 border border-gray-200 rounded-xl text-sm text-gray-500 outline-none cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Preset Role Select */}
              <div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider select-none">
                      Rol / Pozisyon
                    </label>

                    {/* Inline role creator trigger */}
                    {isCreatingCustomRole ? (
                      <button
                        type="button"
                        onClick={() => setIsCreatingCustomRole(false)}
                        className="text-xs text-red-500 hover:text-red-600 font-bold cursor-pointer"
                      >
                        İptal
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setNewRoleName("");
                          setNewRolePermissions(StaffPermissionPreset.STAFF);
                          setIsCreatingCustomRole(true);
                        }}
                        className="text-xs text-blue-600 hover:text-blue-700 font-bold cursor-pointer"
                      >
                        + Yeni Özel Rol Tanımla
                      </button>
                    )}
                  </div>

                  {/* Dynamic Role Creator Form (Inline accordion) */}
                  {isCreatingCustomRole && (
                    <div className="border border-blue-100 rounded-xl p-3.5 bg-blue-50/20 space-y-3 animate-in slide-in-from-top-2 duration-200">
                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Özel Rol İsmi
                        </label>
                        <input
                          type="text"
                          placeholder="Örn: Garson Şefi, Dönerci"
                          value={newRoleName}
                          onChange={(e) => setNewRoleName(e.target.value)}
                          className="w-full h-10 px-3 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600/10"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                          Bu Rolün Yetkileri
                        </label>
                        <div className="grid grid-cols-1 gap-2 max-h-[140px] overflow-y-auto pr-1">
                          {PERMISSIONS.map((perm) => {
                            const isChecked = (newRolePermissions & perm.bit) !== 0;
                            return (
                              <label
                                key={perm.bit}
                                className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none"
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => {
                                    setNewRolePermissions(
                                      isChecked
                                        ? newRolePermissions & ~perm.bit
                                        : newRolePermissions | perm.bit,
                                    );
                                  }}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span>{perm.name}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>

                      <button
                        type="button"
                        disabled={!newRoleName.trim() || createRoleMutation.isPending}
                        onClick={() => {
                          createRoleMutation.mutate({
                            name: newRoleName.trim(),
                            permissions: newRolePermissions,
                          });
                        }}
                        className="w-full h-9 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                      >
                        {createRoleMutation.isPending ? (
                          <Loader2 className="size-3.5 animate-spin" />
                        ) : (
                          "Rolü Kaydet ve Seç"
                        )}
                      </button>
                    </div>
                  )}

                  {!isCreatingCustomRole && (
                    <>
                      <select
                        value={editRoleId || "system_staff"}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "system_admin") {
                            setEditRole("admin");
                          } else {
                            setEditRole("member");
                          }
                          setEditRoleId(val || "system_staff");
                        }}
                        className="w-full h-11 px-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 outline-none focus:bg-white focus:ring-2 focus:ring-blue-600/10 focus:border-blue-600 transition-all duration-200"
                      >
                        <option value="system_staff">Personel / Garson (Varsayılan)</option>
                        <option value="system_cashier">Kasiyer (Varsayılan)</option>
                        <option value="system_admin">Yönetici (Varsayılan)</option>
                        {customRoles.map((role: any) => (
                          <option key={role.id} value={role.id}>
                            {role.name}
                          </option>
                        ))}
                      </select>

                      {/* Read-only Permissions Preview */}
                      <div className="border border-gray-200/60 rounded-xl p-3 bg-gray-50/50">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider select-none mb-2">
                          Bu Rolün Sahip Olduğu Yetkiler
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {(() => {
                            const selectedVal = editRoleId || "system_staff";
                            let perms: number = StaffPermissionPreset.STAFF;
                            if (selectedVal === "system_cashier")
                              perms = StaffPermissionPreset.CASHIER;
                            else if (selectedVal === "system_admin")
                              perms = StaffPermissionPreset.ADMIN;
                            else {
                              const customRole = customRoles.find((r: any) => r.id === selectedVal);
                              if (customRole) perms = customRole.permissions;
                            }

                            const activePerms = PERMISSIONS.filter((p) => (perms & p.bit) !== 0);
                            if (activePerms.length === 0) {
                              return (
                                <span className="text-[10px] text-gray-400">
                                  Hiçbir yetki atanmamış.
                                </span>
                              );
                            }
                            return activePerms.map((p) => (
                              <span
                                key={p.bit}
                                className="inline-flex text-[10px] font-bold text-blue-700 bg-blue-50/50 border border-blue-100 px-2 py-0.5 rounded-md"
                              >
                                {p.name}
                              </span>
                            ));
                          })()}
                        </div>
                      </div>

                      <p className="text-[10px] text-gray-400 flex items-start gap-1 bg-gray-50 p-2 rounded-lg border border-gray-100">
                        <Info className="size-3.5 text-blue-500 shrink-0 mt-0.5" />
                        <span>
                          {editRole === "admin"
                            ? "Yöneticiler hem web yönetim paneline hem de POS terminallerine giriş yapabilir."
                            : "Personeller web yönetim paneline giriş yapamaz, sadece POS terminalini kullanabilir."}
                        </span>
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-3 border-t border-gray-100 mt-4">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="h-11 px-4 border border-gray-200 text-gray-500 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="h-11 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold flex items-center gap-1.5 shadow-md hover:shadow-lg disabled:opacity-50 active:scale-[0.98] transition-all cursor-pointer"
                >
                  {updateMutation.isPending && (
                    <Loader2 className="size-4 animate-spin text-white" />
                  )}
                  <span>Kaydet</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
